import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import { useTheme } from "@material-ui/core/styles";
import PersonIcon from "@material-ui/icons/Person";
import GetAppIcon from "@material-ui/icons/GetApp";
import toastError from "../../errors/toastError";
import { createContact } from "../../services/contacts.api";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";
import NewTicketModal from "../NewTicketModal";

/**
 * Parsea el body del mensaje en un array de { name, number, vcardBlock }.
 * Soporta:
 *   1. Formato vCard completo  (BEGIN:VCARD ... END:VCARD)
 *   2. Formato compacto        ("Nombre: número\n---\nNombre: número")
 */
function parseVcards(body) {
  if (!body) return [];

  // ── Formato vCard completo ──────────────────────────────────────────────────
  if (body.includes("BEGIN:VCARD")) {
    const blocks = body.split(/\n?---\n?/);
    const result = [];
    for (const block of blocks) {
      if (!block.trim()) continue;
      const fnMatch = block.match(/FN:(.*?)(?:\r\n|\r|\n|$)/);
      const waidMatch = block.match(/waid=(\d+)/);
      const telMatch = block.match(/TEL[^:]*:([\+\d\s\-]+)/);
      const name = fnMatch ? fnMatch[1].trim() : "";
      let number = "";
      if (waidMatch) number = waidMatch[1];
      else if (telMatch) number = telMatch[1].replace(/\D/g, "");
      if (name || number) result.push({ name, number, vcardBlock: block });
    }
    return result;
  }

  // ── Formato compacto ("Nombre: número") ────────────────────────────────────
  const lines = body.split(/\n---\n|\n/);
  const result = [];
  for (const line of lines) {
    const trimmed = line.replace(/^---$/, "").trim();
    if (!trimmed) continue;
    const colonIdx = trimmed.lastIndexOf(":");
    if (colonIdx <= 0) {
      result.push({ name: trimmed, number: "", vcardBlock: trimmed });
      continue;
    }
    const name = trimmed.substring(0, colonIdx).trim();
    const number = trimmed.substring(colonIdx + 1).trim().replace(/\D/g, "");
    if (name || number) result.push({ name, number, vcardBlock: trimmed });
  }
  return result;
}

function buildVcardBlob(contacts) {
  const vcf = contacts
    .map((c) => {
      const waid = c.number ? c.number.replace(/\D/g, "") : "";
      return [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `FN:${c.name || ""}`,
        waid ? `TEL;type=CELL;type=VOICE;waid=${waid}:+${waid}` : "",
        "END:VCARD",
      ]
        .filter(Boolean)
        .join("\r\n");
    })
    .join("\r\n");
  return new Blob([vcf], { type: "text/vcard;charset=utf-8" });
}

const VCardContactRow = ({ contact }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const [resolved, setResolved] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!contact.number) return;
    const timer = setTimeout(async () => {
      try {
        const obj = {
          name: contact.name,
          number: contact.number.replace(/\D/g, ""),
          email: "",
        };
        const { data } = await createContact(obj);
        if (data.invalido) { setInvalid(true); return; }
        setResolved(data.alreadyExists ? data.existingContact : obj);
      } catch (err) {
        toastError(err);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [contact.name, contact.number]);

  const handleCloseNewTicket = (ticket) => {
    setNewTicketOpen(false);
    if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
  };

  const textMain = isDark ? "#f9fafb" : "#111827";
  const textSub = isDark ? "#9ca3af" : "#6b7280";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
      <NewTicketModal
        modalOpen={newTicketOpen}
        initialContact={resolved || { name: contact.name, number: contact.number }}
        onClose={handleCloseNewTicket}
      />
      <Avatar
        src={resolved?.profilePicUrl}
        style={{
          width: 38,
          height: 38,
          backgroundColor: generateColor(contact.number || contact.name || ""),
          color: "#fff",
          fontSize: 14,
          fontWeight: "bold",
        }}
      >
        {getInitials(contact.name || "")}
      </Avatar>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography
          style={{ fontWeight: 600, fontSize: 13, color: textMain, lineHeight: 1.3 }}
          noWrap
        >
          {contact.name || "Contacto"}
        </Typography>
        {contact.number && (
          <Typography style={{ fontSize: 11, color: textSub }}>
            +{contact.number}
          </Typography>
        )}
      </div>
      <Button
        size="small"
        variant="text"
        disabled={invalid || !contact.number}
        onClick={() => setNewTicketOpen(true)}
        style={{
          color: invalid ? textSub : "#25d366",
          fontSize: 11,
          fontWeight: 600,
          textTransform: "none",
          minWidth: 0,
          padding: "2px 6px",
          flexShrink: 0,
        }}
      >
        {invalid ? "Sin WA" : "Conversar"}
      </Button>
    </div>
  );
};

const VCardPreview = ({ contact, numbers, message }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";

  const body = message?.body || "";
  let contacts = parseVcards(body);

  // Fallback: props legacy (contact / numbers) del código anterior
  if (contacts.length === 0 && (contact || numbers)) {
    contacts = [{ name: contact || "", number: numbers || "", vcardBlock: "" }];
  }

  const bg = isDark ? "#1f2937" : "#f0f2f5";
  const border = isDark ? "#374151" : "#e5e7eb";
  const textMain = isDark ? "#f9fafb" : "#111827";
  const textSub = isDark ? "#9ca3af" : "#6b7280";

  if (contacts.length === 0) return null;

  const displayCount = contacts.length;
  const previewRows = contacts.slice(0, 3);

  const handleDownload = () => {
    const blob = buildVcardBlob(contacts);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeName = (contacts[0]?.name || "contacto").replace(/[^a-zA-Z0-9_\-]/g, "_");
    a.href = url;
    a.download = `${safeName}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        backgroundColor: bg,
        borderRadius: 10,
        padding: "10px 12px 6px 12px",
        minWidth: 230,
        maxWidth: 300,
        border: `1px solid ${border}`,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <PersonIcon style={{ color: textSub, fontSize: 16 }} />
        <Typography style={{ fontSize: 12, color: textSub, fontWeight: 500, flex: 1 }}>
          {displayCount === 1 ? "Contacto" : `${displayCount} contactos`}
        </Typography>
        <IconButton
          size="small"
          onClick={handleDownload}
          title="Descargar vCard (.vcf)"
          style={{ padding: 4, color: "#25d366" }}
        >
          <GetAppIcon style={{ fontSize: 16 }} />
        </IconButton>
      </div>

      {/* Rows */}
      {previewRows.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Divider style={{ backgroundColor: border, margin: "4px 0" }} />}
          <VCardContactRow contact={c} />
        </React.Fragment>
      ))}

      {displayCount > 3 && (
        <Typography
          style={{
            fontSize: 12,
            color: "#25d366",
            fontWeight: 600,
            marginTop: 6,
            paddingTop: 4,
            borderTop: `1px solid ${border}`,
          }}
        >
          Ver todos ({displayCount})
        </Typography>
      )}
    </div>
  );
};

export default VCardPreview;
